import { apiClient } from "@/api/api-client.ts";
import { formatError } from "@/api/helper.ts";
import type { PasswordFormSchema, ProfileFormSchema, User } from "@/features/dashboard/types/user.ts";
import axios, { AxiosError } from "axios";
import { type ReactNode, createContext, useContext, useEffect, useReducer } from "react";

export type AuthContextType = AuthState & {
	login: (payload: { email: string; password: string }) => Promise<User>;
	logout: () => Promise<void>;
	hasRole: (role: string[]) => boolean;
	hasPermission: (role: string[]) => boolean;
	fetchProfile: () => Promise<User>;
	updateProfile: (payload: ProfileFormSchema) => Promise<User>;
	changePassword: (payload: PasswordFormSchema) => Promise<User>;
	verifyEmail: (payload: {
		verify_url: string;
	}) => Promise<{ message: string }>;
	resendVerification: (payload: {
		email: string;
	}) => Promise<{ message: string }>;
};

type AuthState = {
	user: User | null;
	isLoading: boolean;
	error: Error | null;
};

type AuthAction =
	| { type: "REQUEST" }
	| { type: "SUCCESS"; payload: User }
	| { type: "FAILURE"; payload: AxiosError }
	| { type: "INVALID_CREDENTIALS"; payload: AxiosError }
	| { type: "INVALID_TWO_FACTOR_CODE"; payload: AxiosError }
	| { type: "INVALID_TWO_FACTOR_CHALLENGE"; payload: AxiosError }
	| { type: "LOGOUT" }
	| { type: "IDLE" };

const initialState = {
	user: null,
	isLoading: true,
	error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
	switch (action.type) {
		case "REQUEST":
			return { ...state, isLoading: true, error: null };
		case "SUCCESS":
			return { ...state, isLoading: false, user: action.payload, error: null };
		case "INVALID_CREDENTIALS":
			return { ...state, isLoading: false, error: action.payload };
		case "INVALID_TWO_FACTOR_CODE":
			return { ...state, isLoading: false, error: action.payload };
		case "FAILURE":
			return { ...state, isLoading: false, error: action.payload };
		case "LOGOUT":
			return { ...initialState };
		case "IDLE":
			return { ...state, isLoading: false };
		default:
			return state;
	}
};

const baseUrl = (import.meta.env.VITE_PUBLIC_API_BASE_URL || "http://localhost:8000").replace(/\/api$/, "");
const baseClient = axios.create({
	baseURL: baseUrl,
	headers: {
		"X-Requested-With": "XMLHttpRequest",
		"Content-Type": "application/json",
		Accept: "application/json",
	},
	timeout: 1000 * 15,
	withXSRFToken: true,
	withCredentials: true,
});

const AuthContext = createContext<AuthContextType | null>(null);

const AuthProvider = ({ children }: { children: ReactNode }) => {
	const [state, dispatch] = useReducer(authReducer, initialState);

	const fetchProfile = async (): Promise<User> => {
		try {
			const response = await apiClient.get<{ data: User }>("/profile");

			dispatch({ type: "SUCCESS", payload: response.data.data });

			return response.data.data;
		} catch (error) {
			if (error instanceof AxiosError && error.response) {
				dispatch({ type: "FAILURE", payload: formatError(error) });

				if (error.response.status !== 409) throw error;
			}
		}
	};

	const csrf = async () => await baseClient.get("/sanctum/csrf-cookie");

	const login = async (payload: {
		email: string;
		password: string;
	}): Promise<User> => {
		await csrf();

		try {
			await baseClient.post("/login", payload);

			return await fetchProfile();
		} catch (error) {
			if (error instanceof AxiosError && error.response) {
				dispatch({ type: "INVALID_CREDENTIALS", payload: formatError(error) });

				throw error;
			}

			throw new Error("An error has occurred and we're working to fix the problem! We'll be up and running shortly.");
		}
	};

	const logout = async () => {
		await csrf();

		await baseClient.post("/logout");

		dispatch({ type: "LOGOUT" });
	};

	const hasRole = (roles: string[]) => {
		return roles.some((role) => {
			const userRoles = state.user?.roles.flatMap((r) => r.name) || [];

			return userRoles.includes(role);
		});
	};

	const hasPermission = (permissions: string[]) => {
		return permissions.some((permission) => {
			const userPermissions = state.user?.permissions.flatMap((r) => r.name) || [];

			return userPermissions.includes(permission);
		});
	};

	const updateProfile = async (payload: ProfileFormSchema) => {
		try {
			const response = await apiClient.post<{ data: User }>("/profile", payload);

			dispatch({ type: "SUCCESS", payload: response.data.data });

			return response.data.data;
		} catch (error) {
			if (error instanceof AxiosError && error.response) {
				throw error;
			}

			throw new Error("An error has occurred and we're working to fix the problem! We'll be up and running shortly.");
		}
	};

	const changePassword = async (payload: PasswordFormSchema) => {
		try {
			const response = await apiClient.post<{ data: User }>("/profile/change_password", payload);

			dispatch({ type: "SUCCESS", payload: response.data.data });

			return response.data.data;
		} catch (error) {
			if (error instanceof AxiosError && error.response) {
				throw error;
			}

			throw new Error("An error has occurred and we're working to fix the problem! We'll be up and running shortly.");
		}
	};

	const verifyEmail = async (payload: {
		verify_url: string;
	}) => {
		try {
			const response = await apiClient.post<{ message: string }>("/email_verify", payload);

			return response.data;
		} catch (error) {
			if (error instanceof AxiosError && error.response) {
				throw error;
			}

			throw new Error("An error has occurred and we're working to fix the problem! We'll be up and running shortly.");
		}
	};

	const resendVerification = async (payload: {
		email: string;
	}) => {
		try {
			const response = await apiClient.post<{ message: string }>("/resend_verification", payload);

			return response.data;
		} catch (error) {
			if (error instanceof AxiosError && error.response) {
				throw error;
			}

			throw new Error("An error has occurred and we're working to fix the problem! We'll be up and running shortly.");
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <No need to include fetchProfile>
	useEffect(() => {
		const checkAuthStatus = async () => {
			dispatch({ type: "REQUEST" });

			await fetchProfile();
		};

		checkAuthStatus();
	}, []);

	const contextValue: AuthContextType = {
		...state,
		login,
		logout,
		hasRole,
		hasPermission,
		fetchProfile,
		updateProfile,
		verifyEmail,
		resendVerification,
		changePassword,
	};

	return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
	const context = useContext(AuthContext);

	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

export function HasRole({
	children,
	roles,
}: {
	children: React.ReactNode;
	roles: string[];
}) {
	const { hasRole } = useAuth();

	if (!hasRole(roles) && roles.length > 0) {
		return null;
	}

	return children;
}

export function HasPermission({
	children,
	permissions,
}: {
	children: React.ReactNode;
	permissions: string[];
}) {
	const { hasPermission } = useAuth();

	if (!hasPermission(permissions) && permissions.length > 0) {
		return null;
	}

	return children;
}

export { AuthProvider, useAuth };
